import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Play, Volume2, VolumeX, Sparkles } from "lucide-react";
import { HOME } from "@/constants/testIds";

/**
 * VideoCard — premium video card that auto-plays a real .mp4 if it exists
 * at `src`, otherwise renders a premium placeholder.
 *
 * Placement rule (as requested by the user):
 *   Drop store1.mp4 / store2.mp4 into `public/assets/videos/` and the page
 *   will automatically start showing them without any code changes.
 */
const VideoCard = ({ src, title, subtitle, index = 0 }) => {
  const wrapperRef = useRef(null);
  const videoRef = useRef(null);
  const inView = useInView(wrapperRef, { margin: "-15% 0px" });

  const [available, setAvailable] = useState(false);
  const [checked, setChecked] = useState(false);
  const [muted, setMuted] = useState(true);

  // HEAD-check to decide placeholder vs real video.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(src, { method: "HEAD", cache: "no-store" });
        const ct = res.headers.get("content-type") || "";
        if (!cancelled) {
          setAvailable(res.ok && ct.startsWith("video"));
          setChecked(true);
        }
      } catch {
        if (!cancelled) {
          setAvailable(false);
          setChecked(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [src]);

  // Pause when out of viewport
  useEffect(() => {
    if (!available || !videoRef.current) return;
    if (inView) {
      videoRef.current.play().catch(() => {});
    } else {
      videoRef.current.pause();
    }
  }, [inView, available]);

  const toggleMute = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !videoRef.current.muted;
    setMuted(videoRef.current.muted);
  };

  return (
    <motion.div
      ref={wrapperRef}
      data-testid={`${HOME.videoCard}-${index}`}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-15% 0px" }}
      transition={{ duration: 0.6, delay: index * 0.08 }}
      className="relative group"
    >
      <div className="relative aspect-[9/12] sm:aspect-[9/13] rounded-[28px] overflow-hidden glass-light shadow-premium border border-white/60">
        {/* Emerald glow ring on hover */}
        <div className="absolute inset-0 pointer-events-none rounded-[28px] transition-shadow group-hover:shadow-emerald-glow" />

        {available ? (
          <>
            <video
              ref={videoRef}
              src={src}
              autoPlay
              muted={muted}
              loop
              playsInline
              preload="metadata"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <button
              onClick={toggleMute}
              aria-label={muted ? "Unmute video" : "Mute video"}
              className="absolute top-3 right-3 z-10 w-10 h-10 rounded-full glass-dark text-white grid place-items-center hover:scale-105 transition-transform"
            >
              {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </button>
            <div className="absolute left-3 bottom-3 right-3 z-10">
              <div className="glass-dark text-white rounded-2xl px-3 py-2 flex items-center justify-between">
                <div>
                  <div className="text-xs font-mono uppercase tracking-widest text-lime-300">
                    Store Success Story
                  </div>
                  <div className="text-sm font-semibold">{title}</div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <PlaceholderInner title={title} subtitle={subtitle} loading={!checked} />
        )}
      </div>
    </motion.div>
  );
};

const PlaceholderInner = ({ title, subtitle, loading }) => (
  <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
    {/* Animated gradient background */}
    <div
      aria-hidden
      className="absolute inset-0"
      style={{
        background:
          "radial-gradient(80% 80% at 30% 20%, rgba(16,185,129,0.35), transparent 60%), radial-gradient(60% 60% at 80% 90%, rgba(163,230,53,0.45), transparent 70%), linear-gradient(160deg, #0f172a, #052e2b)",
      }}
    />
    <div className="grain" aria-hidden />

    {/* Subtle moving orbs */}
    <motion.div
      aria-hidden
      className="absolute -top-10 -left-10 w-40 h-40 rounded-full blur-3xl"
      style={{ background: "rgba(16,185,129,0.5)" }}
      animate={{ x: [0, 30, 0], y: [0, 20, 0] }}
      transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
    />
    <motion.div
      aria-hidden
      className="absolute -bottom-14 -right-10 w-56 h-56 rounded-full blur-3xl"
      style={{ background: "rgba(163,230,53,0.4)" }}
      animate={{ x: [0, -20, 0], y: [0, -25, 0] }}
      transition={{ duration: 9, repeat: Infinity, ease: "easeInOut", delay: 1 }}
    />

    <div className="relative z-10 flex flex-col items-center text-center px-6">
      <motion.div
        animate={{ scale: [1, 1.06, 1] }}
        transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
        className="relative w-20 h-20 rounded-full grid place-items-center"
        style={{
          background: "linear-gradient(135deg, #10b981, #a3e635)",
          boxShadow: "0 20px 40px -12px rgba(16,185,129,0.6), 0 0 0 8px rgba(255,255,255,0.08)",
        }}
      >
        <Play className="w-8 h-8 text-white translate-x-0.5" fill="currentColor" />
        <span
          aria-hidden
          className="absolute inset-0 rounded-full border border-white/40"
          style={{ animation: "ping 2.5s cubic-bezier(0,0,0.2,1) infinite" }}
        />
      </motion.div>

      <div className="mt-6 chip-dark">
        <Sparkles className="w-3 h-3" /> {loading ? "Checking video…" : "Video Coming Soon"}
      </div>
      <div className="mt-3 font-display font-semibold text-white text-lg sm:text-xl">
        {title}
      </div>
      <div className="mt-1 text-xs text-white/70 max-w-xs">{subtitle}</div>
    </div>
  </div>
);

export default VideoCard;
